// @ts-nocheck

/**
 * MSI3 Market Penetration Service
 * 
 * Calculates and tracks market penetration for population centers.
 * Penetration = (in-market enrollments / addressable market) * 100
 * 
 * ALL subscription tiers count equally toward penetration.
 * Out-of-market enrollments are excluded from penetration calculation.
 */

import { db } from '../firebase';
import firebase from 'firebase/compat/app';
import { MSI3_CONSTANTS } from './constants';

export const marketPenetrationService = {
  /**
   * Calculate current market penetration for a city
   * @param {string} cityId - Population center ID
   * @returns {Promise<number>} Penetration percentage (0-100)
   */
  async calculatePenetration(cityId) {
    // Get city data
    const cityDoc = await db.collection('msi3PopulationCenters').doc(cityId).get();
    if (!cityDoc.exists) {
      console.warn(`City ${cityId} not found`);
      return 0;
    }

    const city = cityDoc.data();
    
    // If addressable market not set, return 0
    if (!city.estimatedChronicDiseasePatients) {
      console.warn(`City ${city.cityName} missing addressable market estimate`);
      return 0;
    }

    // Count ALL in-market enrollments (ALL TIERS count equally)
    const inMarketSnapshot = await db
      .collection('msi3PatientEnrollments')
      .where('facilityCityId', '==', cityId)
      .where('countsTowardMarketPenetration', '==', true)
      .get();

    const inMarketCount = inMarketSnapshot.size;

    // Calculate penetration
    const penetration = (inMarketCount / city.estimatedChronicDiseasePatients) * 100;

    // Record historical snapshot
    await this.recordPenetrationSnapshot(cityId, city, inMarketSnapshot);

    return penetration;
  },

  /**
   * Record a penetration snapshot for historical tracking
   */
  async recordPenetrationSnapshot(cityId, city, inMarketSnapshot) {
    // Count tier breakdown
    const tierBreakdown = {
      free: 0,
      small: 0,
      medium: 0,
      large: 0,
    };

    inMarketSnapshot.docs.forEach((doc) => {
      const tier = doc.data().subscriptionTier || 'free';
      if (tierBreakdown.hasOwnProperty(tier)) {
        tierBreakdown[tier]++;
      }
    });

    const inMarketCount = inMarketSnapshot.size;

    // Count out-of-market enrollments
    const outOfMarketSnapshot = await db
      .collection('msi3PatientEnrollments')
      .where('facilityCityId', '==', cityId)
      .where('countsTowardMarketPenetration', '==', false)
      .get();

    const outOfMarketCount = outOfMarketSnapshot.size;
    const totalCount = inMarketCount + outOfMarketCount;

    const penetration = city.estimatedChronicDiseasePatients
      ? (inMarketCount / city.estimatedChronicDiseasePatients) * 100
      : 0;

    // Determine current tier
    const currentTier = penetration <= 33 ? '0-33%' : penetration <= 66 ? '34-66%' : '67-100%';

    await db.collection('msi3MarketPenetrationHistory').add({
      cityId,
      recordedAt: firebase.firestore.FieldValue.serverTimestamp(),
      totalEnrolledPatients: totalCount,
      inMarketEnrolledPatients: inMarketCount,
      outOfMarketEnrolledPatients: outOfMarketCount,
      freeTierPatients: tierBreakdown.free,
      smallTierPatients: tierBreakdown.small,
      mediumTierPatients: tierBreakdown.medium,
      largeTierPatients: tierBreakdown.large,
      addressableMarket: city.estimatedChronicDiseasePatients || 0,
      penetrationPercentage: penetration,
      currentTier,
    });

    return penetration;
  },

  /**
   * Get penetration history for a city
   * @param {string} cityId - Population center ID
   * @param {number} limit - Number of records to return
   * @returns {Promise<Array>} Penetration history records
   */
  async getPenetrationHistory(cityId, limit = 30) {
    const snapshot = await db
      .collection('msi3MarketPenetrationHistory')
      .where('cityId', '==', cityId)
      .orderBy('recordedAt', 'desc')
      .limit(limit)
      .get();

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      recordedAt: doc.data().recordedAt?.toDate(),
    }));
  },

  /**
   * Get current revenue share tier based on penetration
   * @param {number} penetration - Penetration percentage
   * @returns {Object} Revenue tier info
   */
  getRevenueTier(penetration) {
    const { REVENUE_TIERS } = MSI3_CONSTANTS;
    
    if (penetration <= REVENUE_TIERS.LOW_PENETRATION.maxPercent) {
      return {
        tier: 'low',
        label: '0-33%',
        revenueShare: REVENUE_TIERS.LOW_PENETRATION.revenueShare,
        nextThreshold: REVENUE_TIERS.LOW_PENETRATION.maxPercent,
      };
    }
    
    if (penetration <= REVENUE_TIERS.MEDIUM_PENETRATION.maxPercent) {
      return {
        tier: 'medium',
        label: '34-66%',
        revenueShare: REVENUE_TIERS.MEDIUM_PENETRATION.revenueShare,
        nextThreshold: REVENUE_TIERS.MEDIUM_PENETRATION.maxPercent,
      };
    }
    
    return {
      tier: 'high',
      label: '67-100%',
      revenueShare: REVENUE_TIERS.HIGH_PENETRATION.revenueShare,
      nextThreshold: 100,
    };
  },

  /**
   * Get all population centers with current penetration data
   * @param {string} countryCode - Optional filter by country
   * @returns {Promise<Array>} Population centers with penetration data
   */
  async getAllCitiesWithPenetration(countryCode = null) {
    let query = db.collection('msi3PopulationCenters');
    
    if (countryCode) {
      query = query.where('countryCode', '==', countryCode);
    }
    
    const snapshot = await query.get();
    
    const citiesWithPenetration = await Promise.all(
      snapshot.docs.map(async (doc) => {
        const city = { id: doc.id, ...doc.data() };
        const penetration = await this.calculatePenetration(doc.id);
        const revenueTier = this.getRevenueTier(penetration);
        
        return {
          ...city,
          currentPenetration: penetration,
          revenueTier,
        };
      })
    );
    
    return citiesWithPenetration;
  },

  /**
   * Estimate weeks until next penetration threshold
   * @param {string} cityId - Population center ID
   * @returns {Promise<number>} Estimated weeks
   */
  async estimateWeeksToNextThreshold(cityId) {
    // Get last 4 weeks of history
    const history = await this.getPenetrationHistory(cityId, 28);
    
    if (history.length < 7) {
      return null; // Not enough data
    }
    
    // Calculate average weekly growth
    const recentPenetration = history[0]?.penetrationPercentage || 0;
    const oldPenetration = history[history.length - 1]?.penetrationPercentage || 0;
    const weeksOfData = history.length / 7;
    const weeklyGrowth = (recentPenetration - oldPenetration) / weeksOfData;
    
    if (weeklyGrowth <= 0) {
      return null; // No growth or declining
    }
    
    // Find next threshold
    const revenueTier = this.getRevenueTier(recentPenetration);
    const remainingToThreshold = revenueTier.nextThreshold - recentPenetration;
    
    return Math.ceil(remainingToThreshold / weeklyGrowth);
  },
};

